<script lang="ts">
  import GeneralUsps from '$lib/components/GeneralUsps.svelte'
  import type { PageData } from './$types'
  import { MEMBERSHIP_STRINGS } from './strings'
  import {
    Button,
    CircularProgress,
    Dialog,
    Select,
    Stepper,
    StructuredData,
    ToggleButtonGroup,
  } from '@bruce/components'
  import { invalidateAll } from '$app/navigation'
  import type { CityId } from '@bruce/api/common/city'
  import { utils } from '$lib/utils'
  import MembershipCards from '$lib/components/MembershipCards.svelte'
  import Faq from '$lib/components/Faq.svelte'
  import { page } from '$app/stores'
  import type { ContractType } from '$lib/types/contract'
  import type { Tier } from '@bruce/api/common/tier'
  import { STRINGS } from '$lib/constants/strings'

  export let data: PageData

  $: ({
    lang,
    currentCity,
    cities,
    trial,
    sections,
    faqs,
    featuredStudios,
    imageUrl,
    user,
    campaign,
    tiers,
    formattedFaqs,
    subscriptions,
    defaultTier,
  } = data)

  let cityLoading = false
  let showCampaignTerms = false
  let contractType: ContractType = 'monthly'
  let selectedTier: Tier | undefined = defaultTier

  $: localeRoute = utils.getRoute(lang)
  $: cityId = currentCity?.id ?? 1
  $: showCampaignTerms = $page.url.searchParams.has('terms')
  $: baseSubscription = subscriptions.find(
    (subscription) => subscription.subscription_info.tier_name === 'BASE',
  )

  async function changeCity(event: Event) {
    cityLoading = true
    const cityId = parseFloat(
      (event.target as HTMLSelectElement).value,
    ) as CityId

    utils.changeCity(cityId)
    await invalidateAll()
    cityLoading = false
  }
</script>

<StructuredData
  schema={{
    '@type': 'FAQPage',
    mainEntity: formattedFaqs,
  }}
/>
<div class="content">
  <Stepper
    steps={[
      {
        label: STRINGS[lang].MEMBERSHIP_STEPPER_SELECT_MEMBERSHIP,
        completed: false,
      },
      {
        label: STRINGS[lang].MEMBERSHIP_STEPPER_SIGNUP,
        completed: false,
      },
      {
        label: STRINGS[lang].MEMBERSHIP_STEPPER_PAYMENT,
        completed: false,
      },
    ]}
  />
  <h1>{MEMBERSHIP_STRINGS[lang].TITLE}</h1>
  <section class="city-select">
    {MEMBERSHIP_STRINGS[lang].SIGNUP_IN}
    <Select variant="text" value={cityId} on:change={changeCity}>
      {#each cities as city (city.id)}
        <option value={city.id}>{city.name}</option>
      {/each}
    </Select>
  </section>
  <div class="contract-select">
    {#if baseSubscription}
      <div class="savings">
        {MEMBERSHIP_STRINGS[lang].SAVE}
        {Math.floor(
          (1 -
            baseSubscription.subscription_info.commit_price /
              baseSubscription.subscription_info.flex_price) *
            100,
        )}% {MEMBERSHIP_STRINGS[lang].ON} Base
      </div>
    {/if}
    <ToggleButtonGroup block variant="outlined">
      <Button
        rounded
        block
        variant="text"
        type="button"
        on:click={() => (contractType = 'monthly')}
      >
        {STRINGS[lang].MONTHLY}
      </Button>
      <Button
        rounded
        block
        variant="text"
        type="button"
        on:click={() => (contractType = 'annual')}
      >
        {STRINGS[lang].ANNUAL}
      </Button>
    </ToggleButtonGroup>
  </div>
  {#if subscriptions && subscriptions[0]}
    <section>
      <GeneralUsps
        usps={subscriptions[0].general_usps.filter(
          (usp) =>
            // Include committed null in both contract types
            usp.committed === null ||
            (contractType === 'annual'
              ? usp.committed === true
              : usp.committed === false),
        )}
      />
    </section>
  {/if}
  {#if subscriptions}
    {#if cityLoading}
      <CircularProgress indeterminate />
    {:else}
      <MembershipCards
        subscriptions={contractType === 'annual'
          ? subscriptions.filter(
              (subscription) =>
                subscription.subscription_info.commit_length > 0 &&
                subscription.subscription_info.commit_price > 0,
            )
          : subscriptions}
        currency={currentCity?.country?.currency}
        {trial}
        {featuredStudios}
        {imageUrl}
        {tiers}
        {user}
        {campaign}
        {currentCity}
        {contractType}
        bind:selectedTier
      />
    {/if}
  {/if}
  <section class="faq">
    <Faq {faqs} {sections} />
    <p>
      {MEMBERSHIP_STRINGS[lang].MORE_QUESTIONS}
      <a href={localeRoute('/faq')}>{MEMBERSHIP_STRINGS[lang].FAQ_LINK}</a>.
    </p>
  </section>
  <Button
    type="button"
    rounded
    variant="text"
    on:click={() => (showCampaignTerms = true)}
  >
    {MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS}
  </Button>
  <Dialog
    visible={showCampaignTerms}
    on:toggleVisible={(event) => (showCampaignTerms = event.detail.visible)}
  >
    <h2 slot="title">{MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS}</h2>
    <div slot="content">
      <p>{MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS_INTRO}</p>
      <ul class="campaign-terms-list">
        {#each MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS_LIST as term, index (index)}
          <li>{term}</li>
        {/each}
        <li>
          {MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS_READ_MORE}
          <a href={localeRoute('/terms')}
            >{MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS_READ_MORE_LINK}</a
          >.
        </li>
      </ul>
      <p class="campaign-terms-disclaimer">
        {MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS_DISCLAIMER}
      </p>
    </div>
    <div slot="actions">
      <Button
        rounded
        type="button"
        on:click={() => (showCampaignTerms = false)}
      >
        {MEMBERSHIP_STRINGS[lang].CAMPAIGN_TERMS_DISMISS}
      </Button>
    </div>
  </Dialog>
</div>

<style lang="scss">
  @use '@bruce/assets/css/breakpoints';

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 1em;

    @media screen and (max-width: breakpoints.$breakpoint-md) {
      h1 {
        text-align: center;
      }
    }

    .city-select {
      display: flex;
      align-items: center;
      gap: 0.5em;
      margin-bottom: 1em;
    }

    .contract-select {
      display: grid;
      grid-template-columns: 1fr 1fr;

      .savings {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        justify-self: center;
        align-self: center;
        width: max-content;
        grid-column: 2;
        margin-bottom: 0.75em;
        padding: 0.5em 0.75em;
        background-color: var(--savings-background);
        color: var(--savings-text-color);
        border-radius: 2em;

        &:after {
          content: '';
          position: absolute;
          bottom: -1em;
          left: 50%;
          transform: translateX(-50%);
          border-width: 0.5em;
          border-style: solid;
          border-color: var(--savings-background) transparent transparent
            transparent;
        }
      }

      :global(:nth-child(2)) {
        grid-column: 1 / 3;
      }

      @media screen and (max-width: breakpoints.$breakpoint-md) {
        width: calc(100% - 2em);
        max-width: unset;
      }
    }

    .faq {
      max-width: 800px;
      width: 100%;

      @media screen and (max-width: breakpoints.$breakpoint-md) {
        width: calc(100% - 32px);
      }

      p {
        text-align: center;
      }
    }

    .campaign-terms-list {
      font-size: 20px;

      @media screen and (max-width: breakpoints.$breakpoint-sm) {
        font-size: 18px;
      }

      li {
        margin: 1em 0;
      }
    }

    .campaign-terms-disclaimer {
      font-size: 14px;
    }
  }
</style>
